import React, { useEffect, useState } from "react";
import { sdh_ruleVersions, sdh_activateVersion, type RuleVersion } from "../api/sdhAPI";
import { useAuth } from "../hooks/useAuth";

/**
 * Every version of a rule, with the content and the measurement that belong to THAT version, and the
 * ability to make an earlier one active again. Switching is append-only: it records a new version whose
 * content equals the chosen one, so the trail stays truthful rather than being rewound.
 */
const FIELDS: Array<[string, string]> = [
  ["constraint_family", "Rule type"],
  ["condition", "Condition (WHEN)"],
  ["predicate_template", "Predicate (THEN)"],
  ["expected_outcome", "Expected outcome"],
  ["business_reason", "Plain meaning"],
  ["severity", "Severity"],
];

export default function RuleVersionsPanel(
  { entityId, appNm, constraintId, ruleName, onClose, onChanged }:
  { entityId: string; appNm: string; constraintId: string; ruleName?: string;
    onClose: () => void; onChanged: (m: string) => void }
) {
  const { user } = useAuth();
  const [versions, setVersions] = useState<RuleVersion[]>([]);
  const [active, setActive] = useState<number | null>(null);
  const [busy, setBusy] = useState(true);
  const [working, setWorking] = useState(false);
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [cmp, setCmp] = useState<number | null>(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await sdh_ruleVersions(entityId, constraintId);
      setVersions(r.versions || []); setActive(r.active_version ?? null);
      const top = (r.versions || [])[0];
      if (top) setOpen({ [top.version]: true });
    } catch { setVersions([]); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [entityId, constraintId]);   // eslint-disable-line

  const activate = async (v: number) => {
    if (!window.confirm(`Make v${v} the active rule? This is recorded as a new version — nothing is lost.`)) return;
    setWorking(true);
    try {
      const r = await sdh_activateVersion(entityId, appNm, constraintId, v,
        user?.full_name || user?.user_id || user?.email);
      if (r?.status !== "ok") { onChanged(`Could not activate v${v} (${r?.status || "error"}).`); return; }
      const m = r.measured;
      onChanged(`v${v} is now active (saved as v${r.version})` +
        (m?.status === "ok" ? ` — re-measured: flags ${m.violating} of ${m.applicable}.` : "."));
      await load();
    } catch { onChanged("Could not switch version"); }
    finally { setWorking(false); }
  };

  const impactText = (v: RuleVersion) => {
    const i = v.impact;
    if (!i) return "not measured";
    if (i.status === "STALE") return "not measured for this version";
    if (i.applicable_rows == null) return i.status || "—";
    return `flags ${(i.violations ?? 0).toLocaleString()} of ${(i.applicable_rows ?? 0).toLocaleString()}`;
  };
  const base = cmp != null ? versions.find((v) => v.version === cmp) : undefined;

  return (
    <div className="vh-scrim" onClick={onClose}>
      <div className="vh" onClick={(e) => e.stopPropagation()}>
        <style>{CSS}</style>
        <header>
          <div>
            <div className="eyebrow">VERSION HISTORY</div>
            <div className="title">{ruleName || constraintId}</div>
          </div>
          <button className="x" onClick={onClose}>×</button>
        </header>

        <div className="vh-body">
          {busy ? <div className="mut">Loading…</div> : versions.length === 0 ? (
            <div className="mut">No version history recorded yet.</div>
          ) : (
            <>
              <div className="hint">
                {versions.length} version(s). Each keeps its own rule content and its own measurement.
                {cmp != null && <> Comparing against <b>v{cmp}</b> — <button className="lnk" onClick={() => setCmp(null)}>stop comparing</button></>}
              </div>
              {versions.map((v) => {
                const isOpen = !!open[v.version];
                return (
                  <div className={"vcard" + (v.is_active ? " active" : "")} key={v.version}>
                    <div className="vh-head" onClick={() => setOpen((o) => ({ ...o, [v.version]: !o[v.version] }))}>
                      <span className="vn">v{v.version}</span>
                      {v.is_active && <span className="act">ACTIVE</span>}
                      <span className="ct">{v.change_type}</span>
                      <span className="imp">{impactText(v)}</span>
                      <span className="who">{v.author}{v.at ? ` · ${String(v.at).replace("T", " ").slice(0, 16)}` : ""}</span>
                      <span className="cv">{isOpen ? "▾" : "▸"}</span>
                    </div>
                    {isOpen && (
                      <div className="vh-body2">
                        {v.summary && <div className="sum">{v.summary}</div>}
                        <div className="flds">
                          {FIELDS.map(([k, label]) => {
                            const val = (v.snapshot || {})[k];
                            const other = base ? (base.snapshot || {})[k] : undefined;
                            const diff = base && base.version !== v.version && String(other ?? "") !== String(val ?? "");
                            if (val == null && !diff) return null;
                            return (
                              <div className={"f" + (diff ? " diff" : "")} key={k}>
                                <span className="fk">{label}</span>
                                <span className="fv">{val == null ? "not set" : String(val)}</span>
                              </div>
                            );
                          })}
                        </div>
                        <div className="vacts">
                          {cmp !== v.version &&
                            <button className="btn ghost" onClick={() => setCmp(v.version)}>Compare against this</button>}
                          {!v.is_active && v.restorable &&
                            <button className="btn go" disabled={working} onClick={() => activate(v.version)}>
                              {working ? "Switching…" : `Make v${v.version} active`}</button>}
                          {!v.restorable && <span className="mut">this version predates snapshot storage — not restorable</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const CSS = `
.vh-scrim{position:fixed;inset:0;z-index:93;background:rgba(14,36,54,.42);display:flex;align-items:center;justify-content:center;padding:24px}
.vh{--line:#e3e8ee;--muted:#6b7c8d;width:min(940px,96vw);max-height:90vh;background:#fff;border-radius:16px;
 display:flex;flex-direction:column;overflow:hidden;box-shadow:0 28px 70px rgba(0,0,0,.3);color:#0E2436}
.vh header{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;background:#0e2436;color:#fff}
.vh .eyebrow{font-size:10px;letter-spacing:.12em;color:#9db4c9}
.vh .title{font-size:16px;font-weight:700;font-family:'Space Grotesk',system-ui,sans-serif}
.vh .x{all:unset;cursor:pointer;font-size:24px;color:#cdd8ea}.vh .x:hover{color:#fff}
.vh-body{overflow:auto;padding:14px 20px 20px}
.mut{color:var(--muted);font-size:13px}
.hint{font-size:12.5px;color:var(--muted);margin-bottom:10px}
.lnk{all:unset;cursor:pointer;color:#0e7c7b}.lnk:hover{text-decoration:underline}
.vcard{border:1px solid var(--line);border-radius:11px;margin-bottom:9px;overflow:hidden}
.vcard.active{border-color:#0e7c7b;box-shadow:0 0 0 2px #d9ecea}
.vh-head{display:flex;align-items:center;gap:10px;padding:10px 13px;cursor:pointer;font-size:12.5px}
.vh-head:hover{background:#f8fafb}
.vh-head .vn{font-family:ui-monospace,monospace;font-weight:700;background:#eef1f5;color:#42586e;border-radius:6px;padding:1px 8px}
.vh-head .act{font-size:9.5px;font-weight:700;letter-spacing:.06em;color:#fff;background:#0e7c7b;border-radius:20px;padding:2px 8px}
.vh-head .ct{color:#0b5f5e}
.vh-head .imp{color:#8a3f38;font-family:ui-monospace,monospace;font-size:11.5px}
.vh-head .who{margin-left:auto;color:var(--muted);font-size:11.5px}
.vh-head .cv{opacity:.45;font-size:10px}
.vh-body2{border-top:1px solid var(--line);padding:11px 13px;background:#fbfcfd}
.sum{font-size:12.5px;color:#33404e;margin-bottom:9px}
.flds{display:flex;flex-direction:column;gap:5px}
.flds .f{display:grid;grid-template-columns:150px 1fr;gap:10px;font-size:12.5px;padding:4px 7px;border-radius:6px}
.flds .f.diff{background:#f1faf5;box-shadow:inset 2px 0 0 #1f9d6b}
.flds .fk{font-size:10.5px;letter-spacing:.06em;text-transform:uppercase;color:#9aa6b2;font-weight:700;padding-top:2px}
.flds .fv{font-family:ui-monospace,monospace;color:#33404e;white-space:pre-wrap;word-break:break-word;line-height:1.5}
.vacts{display:flex;justify-content:flex-end;align-items:center;gap:9px;margin-top:10px}
.btn{all:unset;cursor:pointer;padding:7px 14px;border-radius:8px;font-size:12.5px;font-weight:600}
.btn.ghost{background:#eef1f5;color:#42586e}.btn.go{background:#0e7c7b;color:#fff}
.btn[disabled]{opacity:.5;cursor:not-allowed}
`;
